import { supabase } from "./supabase";

/**
 * O IO da aba Catálogo (000253-000256).
 *
 * ---------------------------------------------------------------------------
 * ZERO regra aqui
 * ---------------------------------------------------------------------------
 * Nada neste arquivo decide o que pode. A CONFERÊNCIA dos sete lugares é
 * derivada no BANCO, dentro de `cat_admin_catalogo()`; as recusas, os motivos
 * obrigatórios e as duas metades da decisão de venda são das RPCs. Aqui só se
 * tipa, chama e devolve a frase que o banco mandou.
 *
 * Isso não é purismo: o catálogo é lido por `aplicar_evento`, por
 * `vw_minha_vitrine` e pela esteira, e uma segunda definição de "este produto
 * está completo?" escrita em TypeScript divergiria da primeira em silêncio —
 * é o defeito de "agregar acesso em JavaScript" com outra roupa.
 *
 * ---------------------------------------------------------------------------
 * Nenhum `select` cru
 * ---------------------------------------------------------------------------
 * `cards`, `produto_cards`, `produto_ids_externos`, `ofertas_sem_concessao` e
 * `catalogo_mudancas` têm `revoke all` para `authenticated`. A leitura sai por
 * função com o portão dentro — a lição da 000136, em que um `select` cru numa
 * tabela cuja policy tinha `or eh_suporte()` pôs os 6.338 documentos emitidos
 * de todos os alunos na tela de um só.
 *
 * ---------------------------------------------------------------------------
 * Uma ida para o catálogo inteiro
 * ---------------------------------------------------------------------------
 * São ~240 linhas ao todo (12 produtos, 33 cards, 61 pares, 92 ids, 6 seções,
 * 14 ofertas decididas). Paginar isto seria paginar um índice — a cicatriz da
 * `rt_lista_artigos`, que paginava a ESTRUTURA de uma lei e escondia a
 * existência de três livros. O razão das mudanças é a exceção, e por isso tem
 * função própria: ele cresce, o catálogo não.
 */

/** Um dos sete lugares em que um produto vive, quando está vazio. */
export type LugarQueFalta = {
  /** `ids_externos` · `contrato` · `memberkit` · `memberkit_escola` · `forum` · `cards` · `prazo_academico` */
  lugar: string;
  /** O que acontece com o aluno se este lugar continuar vazio. */
  diz: string;
};

export type VarianteDeContrato = {
  variante: string;
  zapsign_ref: string;
  contrato_url: string;
  /** Quantas ofertas (ids externos) assinam esta variante. */
  ofertas: number;
};

export type ProdutoDoCatalogo = {
  id: string;
  slug: string;
  nome: string;
  tipo: "curso" | "pos" | "mba" | "formacao";
  curso_slug: string;
  curso_nome: string;
  ativo: boolean;
  prazo_tipo: "meses" | "data_fixa" | "vitalicio";
  prazo_meses: number | null;
  prazo_data_fixa: string | null;
  exige_contrato: boolean;
  contrato_url: string | null;
  zapsign_ref: string | null;
  /**
   * O link gravado é o REAL da ZapSign (`/verificar/doc/<uuid>`) e não o
   * placeholder (o mesmo caminho SEM o `/doc/`). Quem decide é o banco: a
   * primeira heurística desta casa, escrita no front em 12/08, escondeu o
   * botão "Assinar contrato" exatamente de quem ele servia.
   */
  contrato_serve: boolean;
  mk_membership_level: number | null;
  forum_course: string | null;
  bubble_ref: string | null;
  /** Alunos com acesso VIGENTE — o agregado das fontes, nunca a contagem de linhas. */
  alunos: number;
  ids: number;
  cards: number;
  /**
   * Os BÔNUS DE ACESSO deste SKU (000273): quem comprar ele ganha N meses de
   * cada destino da lista.
   *
   * `auto` separa os dois bônus que têm a mesma forma e significam coisas
   * diferentes — `false` é o cruzado ("comprou a Pós DP, ganha 6 meses de
   * Escola") e `true` é a auto-referência, que estende o prazo do PRÓPRIO
   * produto além do que o catálogo diz. Sem a distinção, "Aprovação Contábil dá
   * 12 meses de Aprovação Contábil" se lê como defeito e alguém apaga a linha
   * que dobra o prazo de 2.708 alunos.
   *
   * Era uma CONTAGEM até 21/09. Virou lista porque a tela passou a editar — e
   * manter as duas seria a mesma coisa dita duas vezes, com uma podendo
   * divergir.
   */
  bonus: { slug: string; nome: string; meses: number; ativo: boolean; auto: boolean }[];
  variantes: VarianteDeContrato[];
  falta: LugarQueFalta[];
};

export type CardDoCatalogo = {
  id: string;
  slug: string;
  nome: string;
  tipo: "porta" | "ferramenta" | "modulo" | "curso_bonus";
  escopo: "curso" | "conta";
  destino: "em-breve" | "url" | "memberkit" | "sso-cfc" | "sso-sentinela" | "interno";
  url: string | null;
  /** A linha que o ALUNO lê na vitrine do curso — três lugares de `Curso.tsx`. */
  descricao: string | null;
  /**
   * O que É esta ferramenta, escrito para o OPERADOR (000257).
   *
   * É OUTRO campo que `descricao`, e a separação é medida: 31 dos 33 cards já
   * tinham a descrição do aluno quando este nasceu. Um texto só faria a
   * documentação interna aparecer na tela de 29.884 alunos no instante em que
   * alguém salvasse.
   */
  sobre: string | null;
  /**
   * Quem MANTÉM esta ferramenta (000262). Nasce nulo nas 33 de propósito — a
   * própria página de governança do ClickUp traz o campo como `[VALIDAR]`.
   *
   * O que impede o campo de virar decoração é a tela inicial CONTAR quantas
   * estão sem dono: aí o vazio é lista de trabalho, e não um traço.
   */
  responsavel: string | null;
  /**
   * Quando a FERRAMENTA mudou pela última vez, e o que mudou nela (000268).
   *
   * Não é o carimbo do cadastro: `catalogo_mudancas` já registra quem mexeu no
   * card, e as duas coisas divergem nos dois sentidos — a ferramenta ganha
   * recurso sem o card mudar, e a descrição é corrigida sem nada de novo para o
   * aluno. Por isso é campo próprio, e as duas colunas andam JUNTAS: uma data
   * sem o que mudou não informa, e o contrário também não.
   *
   * `date`, não carimbo: quem informa é uma pessoa, e `new Date(iso)` sobre uma
   * data sem hora mostra o dia ANTERIOR em São Paulo.
   */
  atualizada_em: string | null;
  atualizacao_nota: string | null;
  ativo: boolean;
  ordem: number;
  universal: boolean;
  produtos: { produto_id: string; slug: string; curso_slug: string }[];
  secoes: { curso_slug: string; titulo: string }[];
};

/**
 * A documentação de um CURSO: o ESPELHO da página do ClickUp (000262).
 *
 * Somente leitura, e isso é decisão, não falta de tela. O dono da página é a
 * equipe de produtos, que a mantém lá; aqui ela é recarregada por
 * `etl/carregar-catalogo-doc.mjs`. Campo editável neste painel seria
 * sobrescrito na carga seguinte, sem erro nenhum — a mesma razão de a aba de
 * Aulas não ter escrita enquanto o ClickUp for a fonte.
 */
export type DocumentacaoDoCurso = {
  curso_slug: string;
  /** A primeira frase — é o que a tela inicial mostra. */
  resumo: string;
  /** O texto de apresentação inteiro. */
  sobre: string;
  objetivo: string | null;
  publico_alvo: string | null;
  perfil_egresso: string | null;
  modalidade: string | null;
  titulo_conferido: string | null;
  professor_lider: string | null;
  universidade: string | null;
  /** Texto, e não número: a página escreve "12 x 179,90", e é isso que ela diz. */
  valor: string | null;
  lancamento: string | null;
  mec_url: string | null;
  /**
   * Os três números da FONTE. Eles existem SÓ para serem comparados com o que
   * o banco faz — quem manda é `pos_prazos_curso`, e a carga horária dali vai
   * IMPRESSA no diploma. Nunca leia daqui para decidir nada.
   */
  carga_horaria_fonte: number | null;
  duracao_minima_fonte: number | null;
  duracao_maxima_fonte: number | null;
  /** O resto da página, na ordem dela. A ementa é a maior parte. */
  secoes: { titulo: string; texto: string }[];
  /** A página do ClickUp de onde ela foi IMPORTADA (000262); nula se nasceu no hub. */
  fonte_url: string | null;
  fonte_pagina_id: string | null;
  /** Quando a carga leu o ClickUp; nula na página que nasceu no hub. */
  lido_em: string | null;
  /** Quando o CONTEÚDO mudou pela última vez. */
  atualizado_em: string;
  /**
   * De onde a página vem (000306). `hub` desde 25/09, pela decisão de Victor de
   * 24/09 — a página é editada aqui, e a carga do ClickUp não a sobrescreve.
   */
  origem: "clickup" | "hub";
  editado_em: string | null;
  editado_por_descricao: string | null;
  /**
   * Onde a página promete uma coisa e o banco faz outra — mesma forma do
   * `falta` dos sete lugares, e pelo mesmo motivo: "400 <> 360" não diz nada a
   * quem não sabe onde esse número é impresso.
   */
  divergencias: { campo: string; fonte: number | null; banco: number | null; diz: string }[];
};

export type IdExterno = {
  id: number;
  origem: "guru" | "guru_marketplace" | "tmb";
  id_externo: string;
  produto_id: string;
  produto_slug: string;
  produto_nome: string;
  curso_slug: string;
  contrato_variante: string | null;
  /** Quantos eventos da esteira já trouxeram este id. 0 = cadastrado e nunca usado. */
  eventos: number;
  ultimo: string | null;
};

export type OfertaSemConcessao = {
  origem: "guru" | "guru_marketplace" | "tmb";
  id_externo: string;
  nome_na_origem: string;
  decidido_por: string;
  decidido_em: string;
  observacao: string | null;
  eventos: number;
  ultimo: string | null;
};

/**
 * A venda que NINGUÉM decidiu: apareceu em evento e não está em nenhuma das
 * duas listas. É a fila de trabalho da sub-aba Vendas.
 *
 * `descricao` sai do payload BRUTO, e é a única coisa que permite decidir: na
 * Guru o nome do produto pode ser "xyzzzz" e quem identifica a venda é o nome
 * da OFERTA; na TMB é o `lancamento`.
 */
export type VendaSemDecisao = {
  origem: "guru" | "guru_marketplace" | "tmb";
  id_externo: string;
  eventos: number;
  primeiro: string;
  ultimo: string;
  descricao: string | null;
  email_do_ultimo: string | null;
};

export type SecaoDaVitrine = {
  id: string;
  titulo: string;
  ordem: number;
  /** Os slugs dos cards que estão nela, na ordem em que a vitrine os desenha. */
  cards: string[];
};

export type CursoDoCatalogo = {
  slug: string;
  nome: string;
  faculdade: boolean;
  produtos: string[];
  /**
   * Alunos com acesso vigente, contados como PESSOAS DISTINTAS no banco
   * (000290). Nunca some o `alunos` dos SKUs na tela: 421 alunos têm os dois
   * SKUs da Escola, e a soma os contava duas vezes.
   */
  alunos: number;
  secoes: SecaoDaVitrine[];
  prazo_academico: {
    curso_slug: string;
    meses: number;
    meses_maximo: number | null;
    carga_horaria_horas: number | null;
    definido_em: string;
  } | null;
  /** `null` = este curso não tem página documentada, e a tela inicial conta. */
  doc: DocumentacaoDoCurso | null;
};

export type Catalogo = {
  ok: true;
  medido_em: string;
  produtos: ProdutoDoCatalogo[];
  cards: CardDoCatalogo[];
  ids: IdExterno[];
  sem_concessao: OfertaSemConcessao[];
  sem_decisao: VendaSemDecisao[];
  cursos: CursoDoCatalogo[];
};

/*
 * A aba global "Mudanças" continua FORA da tela — decisão de Victor em 16/09.
 *
 * O que voltou, em 25/09 (000299, o F7 do Plano de Melhorias de Produtos), é o
 * razão de UMA ferramenta, na página dela: é lá que a pergunta "quem mudou isto
 * e por quê?" é feita. `catalogo_mudancas` nunca parou de gravar — o motivo, o
 * antes e o depois, e quem agiu —, porque é o banco que escreve, não o front. O
 * razão inteiro continua a uma consulta de distância:
 *
 *     node supabase/tests/sql.mjs "select * from cat_admin_mudancas(50, 0);"
 *
 * Está escrito aqui porque o modo de falha de uma aba removida é alguém
 * concluir que o registro parou junto.
 */

/** Uma linha do HISTÓRICO de uma ferramenta, lida do razão do catálogo (000299). */
export type MudancaDaFerramenta = {
  id: number;
  em: string;
  /**
   * O card, um par (produto › card), uma seção (curso › seção › card) ou —
   * desde a 000304 — o CONTEÚDO dela (tabela › card), que é o gatilho das abas
   * de conteúdo que escreve.
   */
  alvo: "card" | "produto_card" | "secao_card" | "conteudo";
  acao: "criou" | "editou" | "ligou" | "desligou" | "removeu" | "gerou";
  /**
   * A frase do BANCO (`cat_resumo_da_mudanca`). A tela a mostra e não a
   * escreve: é a regra das recusas desta aba — duas tabelas de frases
   * divergiriam, e a que divergisse em silêncio seria a que ninguém lê.
   */
  resumo: string;
  por: string;
  motivo: string | null;
};

/**
 * O histórico de UMA ferramenta: o card, os pares e as seções, do mais novo
 * para o mais velho. RPC própria pela lição da 000254 — o catálogo é lido
 * inteiro por quatro telas, e isto interessa a uma.
 */
export async function lerHistoricoDoCard(
  card_slug: string,
): Promise<Resposta & { total?: number; mudancas?: MudancaDaFerramenta[] }> {
  const { data, error } = await supabase.rpc("cat_admin_historico_do_card", {
    p_card_slug: card_slug,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta & { total?: number; mudancas?: MudancaDaFerramenta[] };
}

/**
 * Cria a ferramenta (000299, o F1). Ela nasce do único jeito que não promete
 * nada: EM CONSTRUÇÃO (`destino = 'em-breve'`) e SEM CURSO — ninguém a vê até
 * alguém ligá-la num curso, que é o segundo ato e pede justificativa.
 *
 * O identificador não muda depois: ele vira o endereço, a chave do razão e a do
 * histórico. Quem confere a forma é o banco (a função E a tabela, desde a
 * 000300), e a recusa diz como ele tem de ser.
 */
export async function criarCard(campos: {
  slug: string;
  nome: string;
  descricao?: string | null;
  sobre?: string | null;
  motivo?: string | null;
}): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_criar_card", {
    p_slug: campos.slug,
    p_nome: campos.nome,
    p_descricao: campos.descricao ?? null,
    p_sobre: campos.sobre ?? null,
    p_motivo: campos.motivo ?? null,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

/** A resposta de todo ato. `detalhe` é a frase do BANCO — a tela não a reescreve. */
export type Resposta = {
  ok: boolean;
  motivo?: string;
  detalhe?: string;
  [extra: string]: unknown;
};

/**
 * A rede de segurança para quando nem a RPC nem a rede responderam.
 *
 * Note o que ela NÃO faz: traduzir `motivo` em frase. O banco já manda o
 * `detalhe`, e uma segunda tabela de frases aqui divergiria da primeira — a
 * que divergisse em silêncio seria justamente a que ninguém lê.
 */
export function mensagemDoCatalogo(r: Resposta | null, erro?: string): string {
  if (erro) return erro;
  if (!r) return "não foi possível falar com o servidor";
  if (r.detalhe) return r.detalhe;
  return "não foi possível salvar";
}

export async function lerCatalogo(): Promise<Catalogo | null> {
  const { data, error } = await supabase.rpc("cat_admin_catalogo");
  if (error) throw error;
  // Zero linha para quem não é admin — o `where pode_ver('catalogo')` da função. Nulo
  // aqui não é falha: é a resposta do portão, e a tela diz isso com letra.
  return (data as Catalogo | null) ?? null;
}

/*
 * NÃO existe aqui o IO de VENDA — `decidirVenda` e `tirarIdExterno` saíram em
 * 21/09, com a sub-aba que era a única a chamá-las.
 *
 * As RPCs continuam no banco (`cat_admin_decidir_venda` e
 * `cat_admin_tirar_id_externo`), com os grants, o motivo obrigatório e as duas
 * metades numa transação só, provadas pelo teste 84, pelo sabotador e por
 * `catalogo-no-ar.mjs`. O que saiu foi a ponta do front: função sem chamador é
 * código que não roda, e código que não roda não denuncia divergência — a
 * cicatriz das cinco funções de calendário que o banco tinha com zero chamadas.
 */

/**
 * Quem mantém uma ferramenta. **Nulo não mexe; string vazia LIMPA** — o mesmo
 * contrato das outras RPCs do catálogo, e sem ele não haveria como TIRAR um
 * dono.
 *
 * É RPC própria, e não um parâmetro a mais em `salvarCard`: acrescentar
 * parâmetro àquela função exige `drop` + `create` no banco, que leva os GRANTS
 * junto (a cicatriz da 000137/000142). Definir dono é outro ato, com outra
 * linha no razão.
 */
export async function definirResponsavel(
  card_slug: string,
  responsavel: string,
  motivo?: string | null,
): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_definir_responsavel", {
    p_card_slug: card_slug,
    p_responsavel: responsavel,
    p_motivo: motivo ?? null,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

/**
 * Põe, muda ou tira um BÔNUS DE ACESSO de um produto (000273).
 *
 * `meses` nulo REMOVE, e só a remoção cobra motivo — é a metade que dá menos.
 *
 * O bônus age na PRÓXIMA venda: ele não devolve nem tira acesso de quem já
 * comprou, e a frase do banco diz isso. A expectativa oposta é razoável, e por
 * isso ela é respondida em vez de deixada para alguém descobrir.
 */
export async function definirBonus(
  produto_slug: string,
  bonus_slug: string,
  meses: number | null,
  motivo?: string | null,
): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_definir_bonus", {
    p_produto_slug: produto_slug,
    p_bonus_slug: bonus_slug,
    p_meses: meses,
    p_motivo: motivo ?? null,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

/**
 * O DOCUMENTO DE DIVULGAÇÃO de uma ferramenta (000271).
 *
 * `existe: false` é RESPOSTA, não erro — a tela desenha o convite a gerar.
 *
 * `desatualizado` e `mudou` vêm do BANCO, comparando a fotografia dos campos
 * que alimentaram o modelo com os de agora. Fazer essa conta aqui exigiria o
 * front conhecer a montagem da entrada, que é a segunda definição dela.
 */
export type DivulgacaoDaFerramenta = {
  ok: true;
  existe: boolean;
  texto?: string;
  modelo?: string;
  gerado_em?: string;
  gerado_por_nome?: string | null;
  tokens_in?: number | null;
  tokens_out?: number | null;
  desatualizado?: boolean;
  /** Os campos que mudaram desde a geração. Vazio quando o documento está em dia. */
  mudou?: string[];
};

export async function lerDivulgacao(
  card_slug: string,
): Promise<DivulgacaoDaFerramenta | Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_divulgacao", {
    p_card_slug: card_slug,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as DivulgacaoDaFerramenta | Resposta;
}

/**
 * Manda a Edge Function ESCREVER o documento. É o botão, e nada mais dispara
 * isto — nem cron, nem gatilho, nem a carga: cada rodada custa uma chamada ao
 * modelo, e Victor pediu manual.
 *
 * O detalhe do erro vem do CORPO da resposta, não da mensagem do SDK: a
 * function escreve a frase para o operador ler, e `error.message` diria só
 * "non-2xx status code" (a mesma razão de `drenarAvisos`).
 */
export async function gerarDivulgacao(card_slug: string): Promise<Resposta> {
  const { data, error } = await supabase.functions.invoke("gerar-divulgacao", {
    body: { card: card_slug },
  });
  const corpo = (data ?? {}) as Record<string, unknown>;
  if (error) {
    return {
      ok: false,
      motivo: typeof corpo.motivo === "string" ? corpo.motivo : undefined,
      detalhe: typeof corpo.detalhe === "string" ? corpo.detalhe : error.message,
    };
  }
  return corpo as Resposta;
}

/** Os campos da página que a tela edita (000306). A lista é a do banco, FECHADA. */
export type CamposDaPagina = Partial<{
  resumo: string;
  sobre: string;
  objetivo: string;
  publico_alvo: string;
  perfil_egresso: string;
  modalidade: string;
  titulo_conferido: string;
  professor_lider: string;
  universidade: string;
  valor: string;
  lancamento: string;
  mec_url: string;
  /** Número, ou "" para limpar. */
  carga_horaria_fonte: number | "";
  duracao_minima_fonte: number | "";
  duracao_maxima_fonte: number | "";
  secoes: { titulo: string; texto: string }[];
}>;

/**
 * Edita a página do produto (000306). O contrato é o do catálogo: o campo que
 * NÃO vai no objeto não muda; texto vazio LIMPA — menos o resumo e o texto
 * principal, que o banco recusa vazios. A tela manda só o que mudou.
 */
export async function editarPaginaDoProduto(
  curso: string,
  campos: CamposDaPagina,
  motivo: string | null,
): Promise<Resposta & { campos?: string[] }> {
  const { data, error } = await supabase.rpc("cat_admin_editar_pagina_do_produto", {
    p_curso_slug: curso,
    p_campos: campos,
    p_motivo: motivo,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta & { campos?: string[] };
}

/** Uma turma no CRONOGRAMA do produto (000309). */
export type TurmaDoCronograma = {
  id: string;
  numero: number;
  /** AAAA-MM-DD, ou nula: sem início, a turma não tem cronograma. */
  inicio_em: string | null;
  atual: boolean;
  total: number;
  com_prazo: number;
  liberadas: number;
  pendentes: number;
  /** O MÓDULO é o número que a grade guarda (`pos_grade.modulo`). */
  proxima: { nome: string; modulo: number | null; libera_em: string } | null;
  ultima_liberada: { nome: string; modulo: number | null; libera_em: string } | null;
};

/**
 * O cronograma de liberação de TODAS as turmas de um curso (000309, o G3), para
 * a página do produto. Ele só ACOMPANHA — a disciplina ainda não liberada
 * continua aberta para o aluno. A data é do banco (início + prazo da grade).
 */
export async function lerCronogramaDoProduto(
  curso: string,
): Promise<{ ok: true; hoje: string; turmas: TurmaDoCronograma[] } | (Resposta & { ok: false })> {
  const { data, error } = await supabase.rpc("cat_admin_cronograma_do_produto", { p_curso_slug: curso });
  if (error) return { ok: false, detalhe: error.message };
  return data as { ok: true; hoje: string; turmas: TurmaDoCronograma[] } | (Resposta & { ok: false });
}

/** Uma linha do histórico de atualizações de uma ferramenta (000277). */
export type AtualizacaoDaFerramenta = {
  id: string;
  /** O dia em que a atualização aconteceu — informado por quem registra. */
  quando: string;
  nota: string;
  /** Quem REGISTROU, não necessariamente quem fez a mudança. Pode ser nulo. */
  por: string | null;
  criado_em: string;
  /**
   * A DIVULGAÇÃO ao aluno (000304, o F4). O aviso fica gravado mesmo depois de
   * retirado, para a tela o oferecer se alguém divulgar de novo; quem diz se
   * ele está no ar AGORA é `divulgada_agora`, calculado no banco — os 30 dias
   * moram lá, num lugar só.
   */
  aviso_ao_aluno: string | null;
  divulgada_em: string | null;
  divulgada_ate: string | null;
  divulgada_agora: boolean;
  divulgada_por: string | null;
};

/**
 * Divulga uma atualização ao aluno: um aviso de até 280 caracteres, que aparece
 * por 30 dias como «Novidade» no card da ferramenta e no alto dela. Não é e-mail
 * nem notificação fora do hub. Quem recusa (aviso vazio, longo, ferramenta em
 * construção) é o banco, com a frase pronta.
 */
export async function divulgarAtualizacao(id: string, texto: string): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_divulgar_atualizacao", { p_id: id, p_texto: texto });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

/** Tira a novidade da área do aluno. Pede MOTIVO: é o ato que dá menos. */
export async function retirarDivulgacao(id: string, motivo: string): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_retirar_divulgacao", { p_id: id, p_motivo: motivo });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

/** Uma fonte do conteúdo da ferramenta — uma tabela do mapa (000304). */
export type FonteDoConteudo = {
  tabela: string;
  /** Como a tela chama a tabela ("parâmetros do MEI"). */
  rotulo: string;
  /** A maior data entre a tabela e as edições registradas; nula = nenhuma. */
  ultima: string | null;
  /** A tabela guarda data por linha? Sem isso, só o registro de 25/09 em diante data. */
  guarda_data: boolean;
};

export type ConteudoDaFerramenta = {
  ok: true;
  tem_conteudo: boolean;
  ultima: string | null;
  ultima_em: string | null;
  fontes: FonteDoConteudo[];
};

/**
 * Quando o CONTEÚDO da ferramenta mudou pela última vez (000304, o F5) — a data
 * automática. Ela sai das tabelas de conteúdo e do registro das edições; o QUE
 * mudou, em palavras, não tem fonte automática e continua sendo escrito por quem
 * registra a atualização.
 */
export async function lerConteudoDaFerramenta(
  card_slug: string,
): Promise<ConteudoDaFerramenta | (Resposta & { ok: false })> {
  const { data, error } = await supabase.rpc("cat_conteudo_da_ferramenta", { p_card_slug: card_slug });
  if (error) return { ok: false, detalhe: error.message };
  return data as ConteudoDaFerramenta | (Resposta & { ok: false });
}

/**
 * O HISTÓRICO de atualizações de uma ferramenta (000277).
 *
 * É RPC própria, e não um campo do catálogo, porque o catálogo é lido inteiro
 * por quatro telas e isto interessa a UMA — a lição da 000254, em que um campo
 * por card levou a leitura a 2,8 s.
 */
export async function lerAtualizacoes(
  card_slug: string,
): Promise<Resposta & { atualizacoes?: AtualizacaoDaFerramenta[] }> {
  const { data, error } = await supabase.rpc("cat_admin_atualizacoes", { p_card_slug: card_slug });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta & { atualizacoes?: AtualizacaoDaFerramenta[] };
}

/**
 * Tira uma linha do histórico — o "desfazer" de um registro errado.
 *
 * Ele exige MOTIVO, e o critério é o da casa: o ato que dá menos é o que cobra
 * explicação. Até 22/09 desfazer era mandar a nota vazia, que apagava data e
 * texto de uma vez; com histórico, isso deixou de ter sentido e o banco passou
 * a recusar a nota vazia dizendo este caminho.
 */
export async function removerAtualizacao(id: string, motivo: string): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_remover_atualizacao", {
    p_id: id,
    p_motivo: motivo,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

/**
 * ACRESCENTA uma atualização ao histórico da ferramenta (000268, virou
 * histórico na 000277). Cada chamada é uma linha nova; `cards.atualizada_em` e
 * `cards.atualizacao_nota` passaram a ser DERIVADAS da mais recente.
 *
 * **Nulo não mexe; string vazia é RECUSADA** — o "vazio limpa" saiu na 000277,
 * porque com histórico ele apagaria a linha errada em silêncio. Desfazer é
 * `removerAtualizacao`.
 *
 * `quando` nulo deixa o BANCO escolher o dia, no fuso de São Paulo. Data no
 * futuro é recusada lá, com a frase pronta: este campo descreve o que já
 * aconteceu.
 */
export async function registrarAtualizacao(
  card_slug: string,
  nota: string,
  quando?: string | null,
  motivo?: string | null,
): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_registrar_atualizacao", {
    p_card_slug: card_slug,
    p_nota: nota,
    p_quando: quando || null,
    p_motivo: motivo ?? null,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

/**
 * Edita um produto. **Nulo não mexe; string vazia limpa** — o contrato da
 * 000247, escrito depois de o editor de docentes apagar o contato de quem
 * salvasse só o nome.
 *
 * `contrato` aceita o link inteiro da ZapSign ou só a referência: adivinhar
 * qual dos dois veio é trabalho do banco, não de quem copia da tela de lá.
 */
export async function salvarProduto(campos: {
  slug: string;
  nome?: string | null;
  curso_nome?: string | null;
  prazo_tipo?: string | null;
  prazo_meses?: number | null;
  prazo_data_fixa?: string | null;
  exige_contrato?: boolean | null;
  contrato?: string | null;
  mk_membership_level?: number | null;
  forum_course?: string | null;
  ativo?: boolean | null;
  motivo?: string | null;
}): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_salvar_produto", {
    p_slug: campos.slug,
    p_nome: campos.nome ?? null,
    p_curso_nome: campos.curso_nome ?? null,
    p_prazo_tipo: campos.prazo_tipo ?? null,
    p_prazo_meses: campos.prazo_meses ?? null,
    p_prazo_data_fixa: campos.prazo_data_fixa ?? null,
    p_exige_contrato: campos.exige_contrato ?? null,
    p_contrato: campos.contrato ?? null,
    p_mk_membership_level: campos.mk_membership_level ?? null,
    p_forum_course: campos.forum_course ?? null,
    p_ativo: campos.ativo ?? null,
    p_motivo: campos.motivo ?? null,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

export async function salvarCard(campos: {
  slug: string;
  nome?: string | null;
  descricao?: string | null;
  sobre?: string | null;
  destino?: string | null;
  url?: string | null;
  ordem?: number | null;
  ativo?: boolean | null;
  motivo?: string | null;
}): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_salvar_card", {
    p_slug: campos.slug,
    p_nome: campos.nome ?? null,
    p_descricao: campos.descricao ?? null,
    p_sobre: campos.sobre ?? null,
    p_destino: campos.destino ?? null,
    p_url: campos.url ?? null,
    p_ordem: campos.ordem ?? null,
    p_ativo: campos.ativo ?? null,
    p_motivo: campos.motivo ?? null,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

/**
 * Liga ou desliga um card num produto — o par que decide o que o curso mostra.
 *
 * Desligar exige motivo, e o banco devolve na recusa QUANTOS alunos perdem a
 * ferramenta. A tela não calcula esse número: ela o mostra.
 */
export async function ligarCard(
  produto_slug: string,
  card_slug: string,
  ligar: boolean,
  motivo?: string | null,
): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_ligar_card", {
    p_produto_slug: produto_slug,
    p_card_slug: card_slug,
    p_ligar: ligar,
    p_motivo: motivo ?? null,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

/**
 * Liga ou desliga um card em TODOS os produtos de um curso, numa transação só
 * (000269). É esta que a tela usa.
 *
 * `ligarCard`, acima, continua existindo e mexe num SKU: ela é a que decide
 * todas as regras — o portão, a recusa do universal, o motivo obrigatório, a
 * contagem de quem perde a ferramenta —, e esta a chama por produto. A tela não
 * a usa porque o par é (produto, card) e a Escola tem dois SKUs com as MESMAS
 * ferramentas: um botão por SKU deixaria a Vitalícia para trás na primeira
 * ferramenta nova, sem ninguém ver.
 */
export async function ligarCardNoCurso(
  curso_slug: string,
  card_slug: string,
  ligar: boolean,
  motivo?: string | null,
): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_ligar_card_no_curso", {
    p_curso_slug: curso_slug,
    p_card_slug: card_slug,
    p_ligar: ligar,
    p_motivo: motivo ?? null,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

export async function salvarSecao(campos: {
  curso_slug: string;
  titulo: string;
  id?: string | null;
  ordem?: number | null;
  motivo?: string | null;
}): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_salvar_secao", {
    p_curso_slug: campos.curso_slug,
    p_titulo: campos.titulo,
    p_id: campos.id ?? null,
    p_ordem: campos.ordem ?? null,
    p_motivo: campos.motivo ?? null,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

export async function porCardNaSecao(
  secao: string,
  card_slug: string,
  dentro: boolean,
  motivo?: string | null,
): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_secao_card", {
    p_secao: secao,
    p_card_slug: card_slug,
    p_dentro: dentro,
    p_motivo: motivo ?? null,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

export async function removerSecao(secao: string, motivo: string): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_remover_secao", {
    p_secao: secao,
    p_motivo: motivo,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

/*
 * `ROTULO_DA_ORIGEM` e `dataCurta` saíram junto com a sub-aba Vendas (21/09):
 * as duas serviam só a ela. A outra `dataCurta` da casa, a de `lib/admin.ts`,
 * continua onde estava — e ela faz `new Date(iso)`, então não serve para data
 * SEM hora (o comentário de `AdminCatalogoProdutos.tsx` explica o porquê).
 */
